import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

export default function Dashboard({ setActiveTab, academy }) {
  const [userId, setUserId] = useState(null);
  const [stats, setStats] = useState({
    totalPlayers: 0,
    todayPresent: 0,
    todayTotal: 0,
    todayMarked: false,
    feesCollected: 0,
    feesPending: 0,
    feesTotal: 0,
    recentPlayers: [],
    batchCounts: {},
  });
  const [loading, setLoading] = useState(true);

  const today = new Date();
  const dateStr = today.toISOString().split("T")[0];
  const month = today.getMonth() + 1;
  const year = today.getFullYear();

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => setUserId(user?.id));
  }, []);

  useEffect(() => { if (userId) fetchStats(); }, [userId]);

  const fetchStats = async () => {
    const [
      { data: players },
      { data: todayAttendance },
      { data: feeRecords },
    ] = await Promise.all([
      supabase.from("players").select("*").eq("user_id", userId).order("created_at", { ascending: false }),
      supabase.from("attendance").select("*").eq("date", dateStr),
      supabase.from("fee_records").select("*").eq("month", month).eq("year", year),
    ]);

    const allPlayers = players || [];
    const attendance = todayAttendance || [];
    const fees = feeRecords || [];

    // Batch counts
    const batchCounts = {};
    allPlayers.forEach((p) => {
      if (p.batch) batchCounts[p.batch] = (batchCounts[p.batch] || 0) + 1;
    });

    // Fee stats
    const paidIds = fees.filter((f) => f.status === "paid").map((f) => f.player_id);
    const feesCollected = allPlayers
      .filter((p) => paidIds.includes(p.id))
      .reduce((sum, p) => sum + (Number(p.fee_amount) || 0), 0);
    const feesTotal = allPlayers.reduce((sum, p) => sum + (Number(p.fee_amount) || 0), 0);

    setStats({
      totalPlayers: allPlayers.length,
      todayPresent: attendance.filter((a) => a.status === "present").length,
      todayTotal: attendance.length,
      todayMarked: attendance.length > 0,
      feesCollected,
      feesPending: feesTotal - feesCollected,
      feesTotal,
      recentPlayers: allPlayers.slice(0, 3),
      batchCounts,
    });
    setLoading(false);
  };

  const feePct = stats.feesTotal > 0
    ? Math.round((stats.feesCollected / stats.feesTotal) * 100)
    : 0;

  const attendancePct = stats.todayTotal > 0
    ? Math.round((stats.todayPresent / stats.todayTotal) * 100)
    : 0;

  const greetingHour = today.getHours();
  const greeting = greetingHour < 12 ? "Good morning" : greetingHour < 17 ? "Good afternoon" : "Good evening";

  return (
    <div style={s.container}>

      {/* HEADER */}
      <div style={s.header}>
        <div style={s.logoRow}>
          <div style={s.logoIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M2 17l10 5 10-5" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M2 12l10 5 10-5" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
            </svg>
          </div>
          <span style={s.logoText}>{academy?.name?.toUpperCase() || "THE COACH ROOM"}</span>
        </div>
        <div style={s.greeting}>{greeting}, Coach 🏏</div>
        <div style={s.academyName}>{academy?.name}{academy?.city ? ` · ${academy.city}` : ""}</div>
        <div style={s.dateStr}>{DAYS[today.getDay()]}, {today.getDate()} {MONTHS[today.getMonth()]} {today.getFullYear()}</div>
      </div>

      {loading ? (
        <div style={s.loading}>Loading your dashboard...</div>
      ) : (
        <div style={s.body}>

          {/* TOP STATS ROW */}
          <div style={s.statsRow}>
            <div style={s.statCard} onClick={() => setActiveTab("players")}>
              <div style={s.statIcon}>👥</div>
              <div style={s.statNum}>{stats.totalPlayers}</div>
              <div style={s.statLabel}>Players</div>
            </div>
            <div style={s.statCard} onClick={() => setActiveTab("attendance")}>
              <div style={s.statIcon}>📅</div>
              <div style={{ ...s.statNum, color: stats.todayMarked ? "#38a169" : "#9ca3af" }}>
                {stats.todayMarked ? stats.todayPresent : "—"}
              </div>
              <div style={s.statLabel}>Today Present</div>
            </div>
            <div style={s.statCard} onClick={() => setActiveTab("fees")}>
              <div style={s.statIcon}>💰</div>
              <div style={{ ...s.statNum, color: "#d4a017", fontSize: "1rem" }}>
                ₹{stats.feesCollected.toLocaleString("en-IN")}
              </div>
              <div style={s.statLabel}>Collected</div>
            </div>
          </div>

          {/* ATTENDANCE CARD */}
          <div style={s.card} onClick={() => setActiveTab("attendance")}>
            <div style={s.cardHeader}>
              <div style={s.cardTitle}>Today's Attendance</div>
              <div style={s.cardAction}>View →</div>
            </div>
            {stats.todayMarked ? (
              <>
                <div style={s.bigStat}>
                  <span style={{ color: "#38a169", fontWeight: "800" }}>{stats.todayPresent}</span>
                  <span style={{ color: "#9ca3af", fontSize: "1.2rem" }}> / {stats.todayTotal} marked</span>
                </div>
                <div style={s.barTrack}>
                  <div style={{ ...s.barFill, width: `${attendancePct}%`, background: "linear-gradient(90deg, #1a3a1b, #38a169)" }} />
                </div>
                <div style={s.barLabel}>{attendancePct}% present today</div>
              </>
            ) : (
              <div style={s.notDone}>
                <span style={s.notDoneIcon}>⏳</span>
                <div>
                  <div style={s.notDoneText}>Attendance not marked yet</div>
                  <div style={s.notDoneSub}>Tap to mark today's session</div>
                </div>
              </div>
            )}
          </div>

          {/* FEE CARD */}
          <div style={s.card} onClick={() => setActiveTab("fees")}>
            <div style={s.cardHeader}>
              <div style={s.cardTitle}>Fees · {MONTHS[today.getMonth()]} {year}</div>
              <div style={s.cardAction}>View →</div>
            </div>
            <div style={s.feeRow}>
              <div style={s.feeStat}>
                <div style={{ ...s.feeNum, color: "#38a169" }}>₹{stats.feesCollected.toLocaleString("en-IN")}</div>
                <div style={s.feeLabel}>Collected</div>
              </div>
              <div style={s.feeDivider} />
              <div style={s.feeStat}>
                <div style={{ ...s.feeNum, color: "#e53e3e" }}>₹{stats.feesPending.toLocaleString("en-IN")}</div>
                <div style={s.feeLabel}>Pending</div>
              </div>
            </div>
            <div style={s.barTrack}>
              <div style={{ ...s.barFill, width: `${feePct}%`, background: "linear-gradient(90deg, #1a3a1b, #d4a017)" }} />
            </div>
            <div style={s.barLabel}>{feePct}% collected this month</div>
          </div>

          {/* BATCH BREAKDOWN */}
          {Object.keys(stats.batchCounts).length > 0 && (
            <div style={s.card}>
              <div style={s.cardHeader}>
                <div style={s.cardTitle}>Batches</div>
              </div>
              <div style={s.batchList}>
                {Object.entries(stats.batchCounts).map(([batch, count]) => (
                  <div key={batch} style={s.batchRow}>
                    <div style={s.batchName}>{batch}</div>
                    <div style={s.batchBar}>
                      <div style={{
                        ...s.batchFill,
                        width: `${(count / stats.totalPlayers) * 100}%`,
                      }} />
                    </div>
                    <div style={s.batchCount}>{count}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* RECENT ENROLLMENTS */}
          {stats.recentPlayers.length > 0 && (
            <div style={s.card} onClick={() => setActiveTab("players")}>
              <div style={s.cardHeader}>
                <div style={s.cardTitle}>Recently Enrolled</div>
                <div style={s.cardAction}>All players →</div>
              </div>
              {stats.recentPlayers.map((p) => (
                <div key={p.id} style={s.recentRow}>
                  <div style={s.recentAvatar}>
                    {p.name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
                  </div>
                  <div style={s.recentInfo}>
                    <div style={s.recentName}>{p.name}</div>
                    <div style={s.recentMeta}>{p.batch || "No batch"} · {p.student_id}</div>
                  </div>
                  <div style={s.recentFee}>
                    ₹{Number(p.fee_amount || 0).toLocaleString("en-IN")}
                    <div style={s.recentFeeLabel}>/mo</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* QUICK ACTIONS */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <div style={s.cardTitle}>Quick Actions</div>
            </div>
            <div style={s.quickActions}>
              <button style={s.quickBtn} onClick={() => setActiveTab("attendance")}>
                <span style={s.quickIcon}>📅</span>
                <span>Mark Attendance</span>
              </button>
              <button style={s.quickBtn} onClick={() => setActiveTab("fees")}>
                <span style={s.quickIcon}>💰</span>
                <span>Check Fees</span>
              </button>
              <button style={s.quickBtn} onClick={() => setActiveTab("players")}>
                <span style={s.quickIcon}>👥</span>
                <span>View Players</span>
              </button>
            </div>
          </div>

          <div style={{ height: "1rem" }} />
        </div>
      )}
    </div>
  );
}

const s = {
  container: { flex: 1, overflowY: "auto", background: "#f0ebe0", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", minHeight: "100%" },
  header: { background: "#111a12", padding: "1.4rem 1.5rem 1.8rem" },
  logoRow: { display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1.2rem" },
  logoIcon: { width: "32px", height: "32px", background: "rgba(212,160,23,0.15)", border: "1px solid rgba(212,160,23,0.4)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center" },
  logoText: { color: "white", fontSize: "0.72rem", fontWeight: "800", letterSpacing: "3px" },
  greeting: { color: "white", fontSize: "1.3rem", fontWeight: "700", marginBottom: "0.2rem" },
  dateStr: { color: "rgba(255,255,255,0.4)", fontSize: "0.8rem", fontWeight: "300" },
  academyName: { color: "#d4a017", fontSize: "0.78rem", fontWeight: "700", letterSpacing: "0.5px", marginBottom: "0.2rem" },
  loading: { textAlign: "center", padding: "4rem 2rem", color: "#6b7280", fontSize: "0.9rem" },
  body: { padding: "1rem 1.2rem" },
  statsRow: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.7rem", marginBottom: "0.7rem" },
  statCard: { background: "white", border: "1px solid #e8e0d0", padding: "1rem 0.6rem", textAlign: "center", cursor: "pointer" },
  statIcon: { fontSize: "1.2rem", marginBottom: "0.4rem" },
  statNum: { fontWeight: "800", fontSize: "1.4rem", lineHeight: 1, color: "#1a3a1b" },
  statLabel: { fontSize: "0.62rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: "0.3rem", fontWeight: "500" },
  card: { background: "white", border: "1px solid #e8e0d0", padding: "1.1rem", marginBottom: "0.7rem", cursor: "pointer" },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.8rem" },
  cardTitle: { fontSize: "0.82rem", fontWeight: "700", color: "#1a3a1b", textTransform: "uppercase", letterSpacing: "0.5px" },
  cardAction: { fontSize: "0.72rem", color: "#d4a017", fontWeight: "700" },
  bigStat: { fontSize: "1.8rem", fontWeight: "700", marginBottom: "0.6rem", lineHeight: 1 },
  barTrack: { height: "6px", background: "#f0ebe0", borderRadius: "3px", overflow: "hidden", marginBottom: "0.4rem" },
  barFill: { height: "100%", borderRadius: "3px", transition: "width 0.8s ease" },
  barLabel: { fontSize: "0.72rem", color: "#9ca3af", fontWeight: "500" },
  notDone: { display: "flex", alignItems: "center", gap: "0.8rem", padding: "0.5rem 0" },
  notDoneIcon: { fontSize: "1.5rem" },
  notDoneText: { fontSize: "0.85rem", fontWeight: "600", color: "#4a5568" },
  notDoneSub: { fontSize: "0.72rem", color: "#9ca3af", marginTop: "0.15rem" },
  feeRow: { display: "flex", alignItems: "center", marginBottom: "0.8rem" },
  feeStat: { flex: 1, textAlign: "center" },
  feeNum: { fontWeight: "800", fontSize: "1.1rem" },
  feeLabel: { fontSize: "0.62rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: "0.2rem" },
  feeDivider: { width: "1px", height: "36px", background: "#e8e0d0" },
  batchList: { display: "flex", flexDirection: "column", gap: "0.6rem" },
  batchRow: { display: "flex", alignItems: "center", gap: "0.8rem" },
  batchName: { fontSize: "0.78rem", fontWeight: "600", color: "#4a5568", width: "70px", flexShrink: 0 },
  batchBar: { flex: 1, height: "6px", background: "#f0ebe0", borderRadius: "3px", overflow: "hidden" },
  batchFill: { height: "100%", background: "#1a3a1b", borderRadius: "3px" },
  batchCount: { fontSize: "0.78rem", fontWeight: "700", color: "#1a3a1b", width: "20px", textAlign: "right" },
  recentRow: { display: "flex", alignItems: "center", gap: "0.8rem", paddingBottom: "0.7rem", marginBottom: "0.7rem", borderBottom: "1px solid #f0ebe0" },
  recentAvatar: { width: "36px", height: "36px", borderRadius: "50%", background: "#1a3a1b", color: "#d4a017", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: "800", flexShrink: 0 },
  recentInfo: { flex: 1, minWidth: 0 },
  recentName: { fontSize: "0.85rem", fontWeight: "700", color: "#1a1a1a" },
  recentMeta: { fontSize: "0.7rem", color: "#9ca3af", marginTop: "0.1rem" },
  recentFee: { textAlign: "right", fontSize: "0.85rem", fontWeight: "800", color: "#1a3a1b", flexShrink: 0 },
  recentFeeLabel: { fontSize: "0.6rem", color: "#9ca3af", fontWeight: "400" },
  quickActions: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.6rem" },
  quickBtn: { display: "flex", flexDirection: "column", alignItems: "center", gap: "0.4rem", padding: "0.8rem 0.4rem", background: "#f0ebe0", border: "1px solid #e8e0d0", cursor: "pointer", fontSize: "0.7rem", fontWeight: "600", color: "#1a3a1b", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  quickIcon: { fontSize: "1.3rem" },
};